import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, ChevronRight, ClipboardCheck, Clock, FileText, GraduationCap, HardHat, Inbox, RefreshCw, Settings,
  ShieldCheck, Tractor,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useInductionSeats } from '../../components/induction/useInductionSeats';
import inductionService from '../../services/induction';
import type { MyEquipmentRecord, MyInductions as MyInductionsData, PackItem } from '../../types/induction';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * The person's own induction record: the company policies they are asked to
 * read and acknowledge, and the machines a supervisor has signed them off on.
 * No licence gate — it is their own record, and the server scopes it to them.
 *
 * This is the one way into inductions from the Dashboard, so whoever signs
 * people off or manages the pack gets the register's sections as a menu here.
 */
export default function MyInductions() {
  const orgTz = useOrgTimezone();
  const seats = useInductionSeats();
  const [data, setData] = useState<MyInductionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    inductionService
      .mine()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load your inductions.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const pack = data?.pack ?? [];
  const equipment = data?.equipment ?? [];
  const acknowledged = pack.filter((p) => p.status === 'acknowledged').length;
  const outstanding = pack.length - acknowledged;

  return (
    <>
      <PageHeader
        title="My inductions"
        subtitle="The company policies you need to read, and the machines you are signed off on."
        icon={<GraduationCap className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {!seats.loading && seats.hasAccess && (
          <ManageMenu canAssess={seats.canAssess} canManagePack={seats.canManagePack} />
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            <div className="h-20 bg-neutral-100 rounded-xl animate-pulse" />
            <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
          </div>
        ) : data && (
          <>
            {pack.length > 0 && (
              <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  {outstanding === 0
                    ? <CheckCircle2 className="h-6 w-6 text-success-600 shrink-0" />
                    : <Clock className="h-6 w-6 text-warning-600 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-neutral-900">
                      {acknowledged} of {pack.length} {pack.length === 1 ? 'policy' : 'policies'} acknowledged
                    </p>
                    <p className="text-sm text-neutral-600">
                      {outstanding === 0
                        ? 'You are up to date. You can re-read any policy at any time.'
                        : `${outstanding} still to read and acknowledge.`}
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-2 rounded-full bg-neutral-100 overflow-hidden">
                  <div
                    className="h-full bg-primary-600 rounded-full"
                    style={{ width: `${Math.round((acknowledged / pack.length) * 100)}%` }}
                  />
                </div>
              </section>
            )}

            {data.pending_requests.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 break-words">
                A supervisor has been asked to sign you off
                {data.pending_requests.length > 1 ? ` (${data.pending_requests.length} requests)` : ''}. They will go through
                the machine with you before you use it.
              </div>
            )}

            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary-600 shrink-0" /> Company policies
              </h2>
              {pack.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-600">
                  Your organisation hasn't published an induction pack yet.
                </div>
              ) : (
                <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
                  {pack.map((item) => <PolicyRow key={item.id} item={item} orgTz={orgTz} />)}
                </ul>
              )}
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <HardHat className="h-5 w-5 text-primary-600 shrink-0" /> Equipment you are signed off on
              </h2>
              {equipment.length === 0 ? (
                <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-600">
                  You are not signed off on any machines yet. Ask your supervisor for an induction before using any
                  equipment.
                </div>
              ) : (
                <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
                  {equipment.map((rec) => <EquipmentRow key={rec.id} rec={rec} orgTz={orgTz} />)}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}

/**
 * The sections of /inductions, for an assessor or HR. The same seats decide
 * which tabs the hub itself shows, so a link here never opens a refusal.
 */
function ManageMenu({ canAssess, canManagePack }: { canAssess: boolean; canManagePack: boolean }) {
  const links: { to: string; label: string; hint: string; icon: ReactNode }[] = [
    { to: '/inductions', label: 'Register', hint: 'Who is signed off on what', icon: <ClipboardCheck className="h-5 w-5" /> },
    { to: '/inductions?tab=equipment', label: 'Equipment inductions', hint: 'Safety content for each machine', icon: <Tractor className="h-5 w-5" /> },
    ...(canAssess ? [{ to: '/inductions?tab=requests', label: 'Requests', hint: 'People waiting to be signed off', icon: <Inbox className="h-5 w-5" /> }] : []),
    ...(canManagePack ? [{ to: '/inductions?tab=policies', label: 'Policies', hint: 'The induction pack', icon: <FileText className="h-5 w-5" /> }] : []),
    ...(canAssess ? [{ to: '/inductions?tab=settings', label: 'Settings', hint: 'Whether sign-off is enforced', icon: <Settings className="h-5 w-5" /> }] : []),
  ];
  return (
    <nav aria-label="Manage inductions" className="space-y-3">
      <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary-600 shrink-0" /> Manage inductions
      </h2>
      <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50">
              <span className="text-primary-600 shrink-0">{l.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-neutral-900 break-words">{l.label}</span>
                <span className="block text-xs text-neutral-600 break-words">{l.hint}</span>
              </span>
              <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function PolicyRow({ item, orgTz }: { item: PackItem; orgTz: string }) {
  return (
    <li>
      <Link
        to={`/my-inductions/policies/${item.id}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50"
      >
        <div className="min-w-0 flex-1">
          <p className="font-medium text-neutral-900 break-words">{item.title}</p>
          {(item.reference_code || item.version_label) && (
            <p className="text-xs text-neutral-500 break-words">
              {[item.reference_code, item.version_label].filter(Boolean).join(' · ')}
            </p>
          )}
          <div className="mt-1.5">
            <PolicyStatus item={item} orgTz={orgTz} />
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
      </Link>
    </li>
  );
}

function PolicyStatus({ item, orgTz }: { item: PackItem; orgTz: string }) {
  if (item.status === 'acknowledged') {
    return (
      <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-success-50 px-2.5 py-0.5 text-xs font-medium text-success-700 break-words">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        Acknowledged {item.acknowledged_at ? formatDateTimeInTz(item.acknowledged_at, orgTz) : ''}
      </span>
    );
  }
  if (item.status === 'updated') {
    return (
      <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-warning-50 px-2.5 py-0.5 text-xs font-medium text-warning-700 break-words">
        <RefreshCw className="h-3.5 w-3.5 shrink-0" />
        Updated — please read again
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
      <Clock className="h-3.5 w-3.5 shrink-0" />
      Due
    </span>
  );
}

function EquipmentRow({ rec, orgTz }: { rec: MyEquipmentRecord; orgTz: string }) {
  // acknowledged_at is only set for an acknowledgement of the CURRENT version.
  const readCurrent = !!rec.acknowledged_at;
  return (
    <li>
      <Link
        to={`/my-inductions/equipment/${rec.induction_id}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50"
      >
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-neutral-900 break-words min-w-0">{rec.title}</p>
            {rec.status === 'expiring' && (
              <span className="rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">Expiring soon</span>
            )}
            {rec.status === 'expired' && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Expired</span>
            )}
          </div>
          <p className="text-xs text-neutral-600 break-words">
            Signed off{rec.assessed_by_name ? ` by ${rec.assessed_by_name}` : ''} on{' '}
            {formatDateTimeInTz(rec.assessed_at, orgTz)}
          </p>
          <p className="text-xs text-neutral-600 break-words">
            {rec.expires_on
              ? `${rec.status === 'expired' ? 'Expired' : 'Expires'} ${formatLocalDateInTz(rec.expires_on, orgTz)}`
              : 'Does not expire'}
          </p>
          <p className={`text-xs break-words ${readCurrent ? 'text-success-700' : 'text-warning-700'}`}>
            {readCurrent
              ? `Safety requirements acknowledged ${formatDateTimeInTz(rec.acknowledged_at as string, orgTz)}`
              : 'Safety requirements not yet acknowledged at the current version — please read them'}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
      </Link>
    </li>
  );
}
